import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterTypes } from '@models/filterEnums.model';
import * as Constants from '@shared/_constants';
import { ConfirmationMessage, ModalFullWidthMode } from '@shared/_constants';
import { Lookup } from '@models/lookup.model';
import { SearchStrategyService } from '@services/search-strategy.service';
import { DialogService } from '@services/dialog.service';
import { StudySearchFilter } from '@models/studySearchFilter.model';
import * as _ from 'lodash-es';
import { Paginator } from 'primeng/paginator';
import { ValidateBvdIdService } from '@services/validate-bvd-id/validate-bvd-id.service';
import { Observable, Subject } from 'rxjs';
import { FilterUtilService } from './shared/filter-util.service';
import { takeUntil } from 'rxjs/operators';
import { FilterCriteria } from './shared/filter-criteria.model';
import { IndustryClassificationsCriteria } from './filter-industry-classifications/filter-industry-classifications.model';
import { YearOfIncorporationCriteria } from './filter-year-of-incorporation/filter-year-of-incorporation.model';
import { WebsiteCriteria } from './filter-website/filter-website.model';
import { AppState } from '../../reducers';
import { select, Store } from '@ngrx/store';
import { SearchFilterService } from './shared/search-filter.service';
import { addSearchFilter, updateSearchFilter } from '../analysis/search-strategy/state/study-search-strategy.actions';
import {
  isListedUnlistedFilterAvailable,
  selectSearchStrategyDatabases,
  selectSearchStrategyFilter,
  selectSearchStrategyFiltersCount,
  selectSearchStrategyId,
} from '../analysis/search-strategy/state/study-search-strategy.selectors';
import { StudySearchStrategyDatabase } from '../../models/studySearchStrategyDatabase.model';
import { selectStudyFilters } from '../study.selectors';
import { loadStudyFilters } from '../study.actions';
import { SearchStrategyAction } from '../analysis/search-strategy/models/search-strategy-aciton.model';
import { updateActionTypeOnModifyFilter } from '../analysis/search-strategy/state/search-strategy.actions';
import { SearchStrategyUpdate } from '../analysis/search-strategy/models/search-strategy-update.model';
import { StudyService } from '@services/study.service';

@Component({
  selector: 'app-add-filter-dialog',
  templateUrl: './add-filter-dialog.component.html',
  styleUrls: ['./add-filter-dialog.component.css'],
  providers: [SearchStrategyService],
  encapsulation: ViewEncapsulation.None,
})
export class AddFilterDialogComponent implements OnInit, OnDestroy {
  @ViewChild(Constants.AddFilterDialogMessages.FOOTER_PAGINATOR, { static: false }) footerPaginator: Paginator;

  readOnly = false;
  filterType: Lookup;
  filterTypes: typeof FilterTypes = FilterTypes;

  searchFilterId: number;

  filter: FilterCriteria;
  filterBefore = '';

  submitClicked = false;
  titleText: string;
  btnAddCloseFilterText: string;
  btnAddContdFilterText: string;
  disableFilterType = false;

  isFullScreen = false;
  isMultiDBSelect = false;

  spinnerVisible$: Observable<boolean>;

  isStatusFilterSelected = false;
  isListedUnlistedFilterAvailable = false;
  searchStrategyId: number;
  searchStrategyDatabases: StudySearchStrategyDatabase[];
  studyFilters: Lookup[] = [];

  searchFiltersCount: number;
  searchFilter: StudySearchFilter;
  private _success = new Subject<string>();
  message = '';
  type = 'success';
  searchStrategyUpdateBeforeChange: SearchStrategyUpdate = null;

  private destroy$ = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private dialogService: DialogService,
    private validateBvdIdService: ValidateBvdIdService,
    private filterUtilService: FilterUtilService,
    private searchFilterService: SearchFilterService,
    private studyService: StudyService,
    private store: Store<AppState>
  ) {
    this.spinnerVisible$ = this.filterUtilService.spinnerVisible$;
  }

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyId))
      .subscribe((id) => {
        this.searchStrategyId = id;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyDatabases))
      .subscribe((databases) => {
        this.searchStrategyDatabases = databases;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(isListedUnlistedFilterAvailable))
      .subscribe((isAvailable) => {
        this.isListedUnlistedFilterAvailable = isAvailable;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyFiltersCount))
      .subscribe((count) => {
        this.searchFiltersCount = count;
      });

    if (this.searchFilterId) {
      this.store
        .pipe(takeUntil(this.destroy$))
        .pipe(select(selectSearchStrategyFilter(this.searchFilterId)))
        .subscribe((filter) => {
          this.searchFilter = filter;
        });
    }

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectStudyFilters))
      .subscribe((filters) => {
        if (filters.length === 0) {
          return;
        }

        this.studyFilters = this.disableFilters(filters);
        this.initializeForm();

        this.filterBefore = JSON.stringify(this.filter);
        this.filterUtilService.spinnerRS.next(false);
      });

    this.loadInitialData();
  }

  initializeForm() {
    if (this.searchFilter) {
      this.btnAddCloseFilterText = Constants.AddFilterDialogMessages.UPDATE_FILTER;
      this.btnAddContdFilterText = Constants.AddFilterDialogMessages.UPDATE_FILTER_AND_CONTINUE;
      this.titleText = Constants.AddFilterDialogMessages.UPDATE_FILTER;

      this.disableFilterType = true;
      this.filterType = this.studyFilters.find((type) => type.lookupId === this.searchFilter.stepFilterTypeId);
      this.filter = this.searchFilter.filter;
    } else {
      this.btnAddCloseFilterText = Constants.AddFilterDialogMessages.ADD_FILTER_AND_CLOSE;
      this.btnAddContdFilterText = Constants.AddFilterDialogMessages.ADD_FILTER_AND_CONTINUE;
      this.titleText = Constants.AddFilterDialogMessages.ADD_FILTER;
    }
  }

  filterTypeChanged() {
    this.submitClicked = false;
    this.filter = this.searchFilterService.getFilterObject(this.filterType.name);
    this.isStatusFilterSelected = this.filterType.name.toLowerCase() === 'status';
  }

  loadInitialData() {
    this.isMultiDBSelect = this.searchStrategyDatabases.length >= 2;

    if (this.studyFilters.length === 0) {
      this.filterUtilService.spinnerRS.next(true);
      this.store.dispatch(loadStudyFilters());
    }
  }

  resetFilterDialog() {
    this.filter = null;
    this.filterType = undefined;
    this.submitClicked = false;
    this.loadInitialData();
  }

  dispalyMessage(message: string, success: boolean) {
    this.type = success ? 'success' : 'danger';
    this._success.next(message);
  }

  addFilter(continueAddingFilters: boolean) {
    const nextStep = async () => {
      const filtersBeforeChange = localStorage.getItem(Constants.LocalStorage.SEARCHFILTERS + this.searchStrategyId.toString());
      if (filtersBeforeChange) {
        this.searchStrategyUpdateBeforeChange.searchFilters = JSON.parse(filtersBeforeChange);
      }

      //Commented code to revert Bug 635401 (multiuser scenario) related changes
      const searchStrategyvalidateForMultiuser = true; //await this.studyService.validateSearchStrategyForMultiuser(this.searchStrategyId, this.searchStrategyUpdateBeforeChange);
      if (searchStrategyvalidateForMultiuser) {
        // && searchStrategyvalidateForMultiuser.item1
        if (continueAddingFilters) {
          this.addFilterClickAndContinue();
        } else {
          this.addFilterClickAndClose();
        }
      }
      /*
      else {
        this._success.subscribe((message) => this.message = message);
        this._success.pipe(
          debounceTime(5000)
        ).subscribe(() => this.message = '');
        var username = searchStrategyvalidateForMultiuser.item2 !== undefined && searchStrategyvalidateForMultiuser.item2 !== null ? ' ' + searchStrategyvalidateForMultiuser.item2.firstName + ' ' + searchStrategyvalidateForMultiuser.item2.lastName : '';
        this.message = ResultMessage.MultiUserOnSearchStrategyError.replace('{user}', username);
        this.dispalyMessage(this.message, false); 
      }
      */
    };

    if (this.filter && this.filter.type === FilterTypes.IdentificationNumber) {
      this.validateBvdNumber(nextStep);
      return;
    }
    nextStep();
  }

  addFilterClickAndContinue() {
    this.submitClicked = true;
    if (this.validateFilter()) {
      this.dispatchFilterAction();
      this.resetFilterDialog();
    }
  }

  addFilterClickAndClose() {
    this.submitClicked = true;
    if (this.validateFilter()) {
      this.dispatchFilterAction();
      this.activeModal.close();
    }
  }

  /* MK Note keeping this here only because architecture is setup to keep the dialog service in main component.*/
  private validateBvdNumber(nextStep: any) {
    this.filterUtilService.spinnerRS.next(true);
    const $sbs = this.validateBvdIdService
      .validateBvdNumbers$(this.searchStrategyId, this.filter.bvdId, this.searchStrategyDatabases)
      .subscribe((result) => {
        if (result.length > 0) {
          this.dialogService
            .confirm(
              Constants.AddFilterDialogMessages.DIALOG_CONFIRM_MSG,
              this.validateBvdIdService.getBvdValidationMsg(result),
              Constants.AddFilterDialogMessages.DIALOG_CONFIRM_RESULT_YES_MSG,
              Constants.AddFilterDialogMessages.DIALOG_CONFIRM_RESULT_NO_MSG
            )
            .then((confirmationResult) => {
              if (!confirmationResult) {
                this.filter.bvdId = this.validateBvdIdService.replaceBvdNumbers(this.filter.bvdId, result);
                nextStep();
              } else {
                this.filter.bvdId = '';
              }
            });
        } else {
          nextStep();
        }
        this.filterUtilService.spinnerRS.next(false);
        $sbs.unsubscribe();
      });
  }

  validateFilter(): boolean {
    return this.filter !== undefined && this.filter.validate();
  }

  private dispatchFilterAction(): void {
    const studySearchFilter = this.searchFilterService.convertFromFilterCriteriaAndType(this.filter, this.filterType);
    let _filter = new FilterCriteria();

    if (this.searchFilter) {
      _filter = this.searchFilterService.changeFilterCriteriaProperty(this.filter);
      // update
      this.store.dispatch(updateActionTypeOnModifyFilter({ action: SearchStrategyAction.UpdateFilter }));
      this.store.dispatch(updateSearchFilter({ id: this.searchFilterId, json: studySearchFilter.stepFilterJson, filterCriteria: _filter }));
    } else {
      // create
      const displayOrder = ++this.searchFiltersCount;
      studySearchFilter.displayOrder = displayOrder;
      this.filter.order = displayOrder;

      _filter = this.searchFilterService.changeFilterCriteriaProperty(this.filter);

      studySearchFilter.filter = _filter;
      this.store.dispatch(updateActionTypeOnModifyFilter({ action: SearchStrategyAction.AddFilter }));
      this.store.dispatch(addSearchFilter({ filter: studySearchFilter }));
    }
  }

  public resize() {
    if (this.isFullScreen) {
      const el_fs_modal = document.getElementsByClassName(ModalFullWidthMode.FULL_SCREEN_CSS_CLASS)[0];
      el_fs_modal.classList.remove(ModalFullWidthMode.FULL_SCREEN_CSS_CLASS);
      el_fs_modal.classList.add(ModalFullWidthMode.CSS_CLASS);
      el_fs_modal.classList.add(ModalFullWidthMode.FULL_SCREEN_EXIT_CSS_CLASS);
    } else {
      const el_modal = document.getElementsByClassName(ModalFullWidthMode.CSS_CLASS)[0];
      el_modal.classList.add(ModalFullWidthMode.FULL_SCREEN_CSS_CLASS);
      el_modal.classList.remove(ModalFullWidthMode.CSS_CLASS);
      el_modal.classList.remove(ModalFullWidthMode.FULL_SCREEN_EXIT_CSS_CLASS);
    }

    this.isFullScreen = !this.isFullScreen;
  }

  public async cancell() {
    let cancelled = false;

    if (this.readOnly) {
      cancelled = true;
    } else {
      if (this.filter) {
        const filterAfter: string = JSON.stringify(this.filter);
        if (filterAfter !== this.filterBefore) {
          const confirmationResult = await this.dialogService.confirm(
            Constants.AddFilterDialogMessages.DIALOG_CONFIRM_MSG,
            ConfirmationMessage.FilterChanged,
            Constants.AddFilterDialogMessages.DIALOG_CONFIRM_RESULT_YES_MSG,
            Constants.AddFilterDialogMessages.DIALOG_CONFIRM_RESULT_NO_MSG
          );
          if (confirmationResult === Constants.ConfirmationResult.yes) {
            if (this.searchFilter && this.filterBefore !== undefined) {
              this.searchFilter.filter.copyInto(this.filterBefore);
              this.filter.copyInto(this.filterBefore);
            }
            cancelled = true;
          }
        } else {
          cancelled = true;
        }
      } else {
        cancelled = true;
      }
    }

    if (cancelled) {
      this.activeModal.close();
    }
  }

  public disableAddFilterButton(filterForm: any) {
    if (this.readOnly) {
      return true;
    }
    if (this.filterType === undefined || _.isEmpty(this.filterType)) {
      return true;
    }

    switch (this.filterType.name) {
      case this.filterTypes.CompanyName:
        return this.filter.selectedCompanies === undefined || _.isEmpty(this.filter.selectedCompanies);
      case this.filterTypes.IndustryClassifications:
        return (
          (<IndustryClassificationsCriteria>this.filter).industryCodeSelectionId === undefined ||
          _.isEmpty((<IndustryClassificationsCriteria>this.filter).industryCodeSelectionId)
        );
      case this.filterTypes.Website:
        return !((<WebsiteCriteria>this.filter).hasWebsite || (<WebsiteCriteria>this.filter).noWebsite);
      case this.filterTypes.YearOfIncorporation:
        if (
          (isNaN((<YearOfIncorporationCriteria>this.filter).onAndAfter) ? null : (<YearOfIncorporationCriteria>this.filter).onAndAfter) == null &&
          (isNaN((<YearOfIncorporationCriteria>this.filter).upToAndIncluding) ? null : (<YearOfIncorporationCriteria>this.filter).upToAndIncluding) ==
            null
        ) {
          return true;
        }
        if (
          (isNaN((<YearOfIncorporationCriteria>this.filter).onAndAfter) ? null : (<YearOfIncorporationCriteria>this.filter).onAndAfter) &&
          (isNaN((<YearOfIncorporationCriteria>this.filter).upToAndIncluding) ? null : (<YearOfIncorporationCriteria>this.filter).upToAndIncluding) &&
          (isNaN((<YearOfIncorporationCriteria>this.filter).onAndAfter) ? null : (<YearOfIncorporationCriteria>this.filter).onAndAfter) >
            (isNaN((<YearOfIncorporationCriteria>this.filter).upToAndIncluding) ? null : (<YearOfIncorporationCriteria>this.filter).upToAndIncluding)
        ) {
          return true;
        }
        return false;
      default:
        return filterForm.invalid;
    }
  }

  disableFilters(filterTypeOptions: Lookup[]): Lookup[] {
    if (this.disableListedUnlistedFilter(filterTypeOptions)) {
      _.remove(filterTypeOptions, { name: FilterTypes.ListedUnlisted });
    }

    return filterTypeOptions;
  }

  disableListedUnlistedFilter(filterTypeOptions: Lookup[]): boolean {
    return _.some(filterTypeOptions, { name: FilterTypes.ListedUnlisted }) && !this.isListedUnlistedFilterAvailable;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
